import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Users } from 'lucide-react';
import { t } from '../lib/i18n';

const CITIES = ['Hyderabad', 'Vijayawada', 'Visakhapatnam', 'Warangal', 'Tirupati', 'Kurnool', 'Nellore', 'Kakinada'];

const SearchWidget = ({ variant = 'hero', defaultExperience = 'stay' }) => {
  const [experience, setExperience] = useState(defaultExperience);
  const [city, setCity] = useState('');
  const [guests, setGuests] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();

  const handleCityChange = (val) => {
    setCity(val);
    if (val.length > 0) {
      setSuggestions(CITIES.filter(c => c.toLowerCase().includes(val.toLowerCase())));
    } else {
      setSuggestions([]);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams({ experience });
    if (city) params.set('city', city);
    if (guests) params.set('guests', guests);
    navigate(`/search?${params.toString()}`);
  };

  if (variant === 'compact') {
    return (
      <div data-testid="search-widget-compact" className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-1.5 shadow-sm">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {['stay', 'celebrate'].map(exp => (
            <button key={exp} onClick={() => setExperience(exp)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${experience === exp ? 'bg-navy text-white' : 'text-gray-600'}`}>
              {exp}
            </button>
          ))}
        </div>
        <input
          type="text" placeholder="City..." value={city}
          onChange={e => handleCityChange(e.target.value)}
          className="flex-1 text-sm outline-none px-2 text-navy placeholder-gray-400"
        />
        <button onClick={handleSearch} className="bg-gold hover:bg-gold-dark text-white p-2 rounded-lg transition-colors">
          <Search size={16} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="search-widget" className="relative">
      <div className="bg-white rounded-2xl p-2 shadow-2xl flex flex-col sm:flex-row gap-2 max-w-3xl">
        {/* Experience Toggle */}
        <div className="flex bg-[#F4F4F2] rounded-xl p-1 shrink-0">
          {[
            { key: 'stay', label: t('search.stay') },
            { key: 'celebrate', label: t('search.celebrate') }
          ].map(({ key, label }) => (
            <button
              key={key}
              data-testid={`search-tab-${key}`}
              onClick={() => setExperience(key)}
              className={`px-5 py-2.5 rounded-[10px] text-sm font-medium transition-all duration-200 ${
                experience === key ? 'bg-navy text-white shadow-sm' : 'text-gray-500 hover:text-navy'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Location */}
        <div className="relative flex-1 flex items-center gap-2.5 px-4 border-l border-gray-100">
          <MapPin size={15} className="text-gold shrink-0" />
          <input
            type="text"
            data-testid="search-city-input"
            placeholder={t('search.location')}
            value={city}
            onChange={e => handleCityChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="flex-1 outline-none text-sm text-navy placeholder-gray-400 bg-transparent py-2"
          />
          {suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
              {suggestions.map(s => (
                <button key={s} onClick={() => { setCity(s); setSuggestions([]); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-navy hover:bg-gold/10 transition-colors">
                  <MapPin size={13} className="inline mr-2 text-gold" />{s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Guests */}
        <div className="flex items-center gap-2.5 px-4 border-l border-gray-100">
          <Users size={15} className="text-gold shrink-0" />
          <select
            data-testid="search-guests-select"
            value={guests}
            onChange={e => setGuests(e.target.value)}
            className="outline-none text-sm text-navy bg-transparent py-2 cursor-pointer"
          >
            <option value="">Any guests</option>
            {[2, 5, 10, 20, 50, 100, 200, 500].map(n => (
              <option key={n} value={n}>{n}+ guests</option>
            ))}
          </select>
        </div>

        {/* Search button */}
        <button
          data-testid="search-submit"
          onClick={handleSearch}
          className="bg-gold hover:bg-gold-dark text-white px-7 py-3 rounded-[14px] font-semibold text-sm flex items-center gap-2 transition-colors duration-200 shrink-0"
        >
          <Search size={15} />
          {t('search.cta')}
        </button>
      </div>
    </div>
  );
};

export default SearchWidget;
